import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useApi } from '@backstage/core-plugin-api';
import { knowledgeApiRef } from '../../api';
import type {
  KnowledgeContextFocus,
  KnowledgeMessage,
  KnowledgeMessageAttachment,
  KnowledgeSession,
} from '../../api';
import {
  KnowledgeChatContext,
  KnowledgeChatContextValue,
} from '../KnowledgeBuilder/ChatContext';

const SESSION_STORAGE_KEY = 'rdap-global-assistant:sessionId';
const GLOBAL_ASSISTANT_AGENT = { id: 'global-assistant' };

// Matches Backstage's default catalog entity route, e.g. /catalog/default/component/my-service
const ENTITY_ROUTE_PATTERN = /^\/catalog\/([^/]+)\/([^/]+)\/([^/]+)/;
const TEMPLATE_ROUTE_PATTERN = /^\/create\/templates\/([^/]+)\/([^/]+)/;

function now() {
  return new Date().toISOString();
}

function pageContextFor(pathname: string, search: string): KnowledgeContextFocus {
  // Backstage's catalog filters (kind, tags, owner, lifecycle, ...) are synced to the
  // URL query string (e.g. ?filters[kind]=component&filters[tags][0]=foo), so pagePath
  // must include `search` or the assistant never sees the filters the user applied.
  const focus: KnowledgeContextFocus = {
    pagePath: pathname + search,
    pageTitle: document.title,
  };
  const templateMatch = TEMPLATE_ROUTE_PATTERN.exec(pathname);
  if (templateMatch) {
    const [, namespace, name] = templateMatch;
    focus.templateRef = `template:${namespace}/${name}`;
  }
  const match = ENTITY_ROUTE_PATTERN.exec(pathname);
  if (match) {
    const [, namespace, kind, name] = match;
    focus.kind = kind;
    focus.entityRef = `${kind}:${namespace}/${name}`;
  }
  return focus;
}

export const GlobalAssistantProvider = ({ children }: PropsWithChildren<{}>) => {
  const api = useApi(knowledgeApiRef);
  const location = useLocation();
  const [session, setSession] = useState<KnowledgeSession>();
  const [messages, setMessages] = useState<KnowledgeMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  const activeRequest = useRef<AbortController>();
  const lastSentLocationKey = useRef<string>();
  const latestLocationKeyRequested = useRef<string>();
  // Derived from the router location, so the header never shows a stale or
  // "General" context while the updateSessionContext round-trip to persist
  // it server-side is in flight.
  const [pageFocus, setPageFocus] = useState<KnowledgeContextFocus>(() =>
    pageContextFor(location.pathname, location.search),
  );

  useEffect(() => {
    setPageFocus(pageContextFor(location.pathname, location.search));
  }, [location.pathname, location.search]);

  useEffect(() => {
    // Some pages (e.g. the catalog list) apply their default filters to the
    // URL asynchronously shortly after mount, rather than as part of the
    // navigation itself. Poll window.location briefly after the path changes
    // so the assistant doesn't get stuck showing the pre-filter URL if that
    // later update isn't reflected by the location above.
    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      const currentSearch = window.location.search;
      if (currentSearch !== location.search) {
        setPageFocus(pageContextFor(window.location.pathname, currentSearch));
      }
      if (attempts >= 5) {
        clearInterval(interval);
      }
    }, 200);
    return () => clearInterval(interval);
  }, [location.pathname, location.search]);

  const createNewSession = useCallback(async () => {
    const response = await api.createSession({
      kind: 'chat',
      agent: GLOBAL_ASSISTANT_AGENT,
    });
    setSession(response.session);
    setMessages(response.messages);
    sessionStorage.setItem(SESSION_STORAGE_KEY, response.session.id);
  }, [api]);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      const cachedId = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (cachedId) {
        try {
          const response = await api.getSession(cachedId);
          if (!cancelled) {
            setSession(response.session);
            setMessages(response.messages);
          }
          return;
        } catch {
          sessionStorage.removeItem(SESSION_STORAGE_KEY);
        }
      }

      try {
        if (!cancelled) {
          await createNewSession();
        }
      } catch (e) {
        console.error('[GlobalAssistant] Unable to start session', e);
      }
    }

    init();
    return () => {
      cancelled = true;
    };
  }, [api, createNewSession]);

  useEffect(() => {
    // Keyed off pageFocus (not the raw router location) so an update caught
    // by the polling fallback above still gets persisted even when the
    // router's own location never changed.
    const locationKey = pageFocus.pagePath ?? '';
    if (!session || lastSentLocationKey.current === locationKey) {
      return;
    }
    lastSentLocationKey.current = locationKey;
    latestLocationKeyRequested.current = locationKey;
    api
      .updateSessionContext(session.id, { focus: pageFocus })
      .then(nextSession => {
        // Discard responses for a page the user has already navigated away from,
        // otherwise a slow request can overwrite the newer page's context.
        if (latestLocationKeyRequested.current === locationKey) {
          setSession(nextSession);
        }
      })
      .catch(e => {
        console.error('[GlobalAssistant] Unable to update page context', e);
      });
  }, [api, session, pageFocus]);

  const resetSession = useCallback(async () => {
    activeRequest.current?.abort();
    activeRequest.current = undefined;
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    setSession(undefined);
    setMessages([]);
    try {
      await createNewSession();
    } catch (e) {
      console.error('[GlobalAssistant] Unable to reset session', e);
    }
  }, [createNewSession]);

  const sendMessage = useCallback(
    async (content: string, attachments?: KnowledgeMessageAttachment[]) => {
      if (!session || !content.trim()) {
        return;
      }

      const userMessage: KnowledgeMessage = {
        id: `local-message-${Date.now()}`,
        sessionId: session.id,
        role: 'user',
        content,
        attachments,
        createdAt: now(),
      };
      setMessages(current => [...current, userMessage]);

      const controller = new AbortController();
      activeRequest.current = controller;
      const requestStartedAt = Date.now();
      setIsSending(true);

      try {
        const response = await api.sendMessage(
          session.id,
          content,
          undefined,
          GLOBAL_ASSISTANT_AGENT,
          controller.signal,
          attachments,
        );
        const durationMs = Date.now() - requestStartedAt;
        setMessages(current => [...current, { ...response.message, durationMs }]);
      } catch (e) {
        if (controller.signal.aborted) {
          setMessages(current => [
            ...current,
            {
              id: `local-error-${Date.now()}`,
              sessionId: session.id,
              role: 'system',
              content: 'Generation stopped.',
              createdAt: now(),
            },
          ]);
          return;
        }
        console.error('[GlobalAssistant] Chat message failed', e);
        setMessages(current => [
          ...current,
          {
            id: `local-error-${Date.now()}`,
            sessionId: session.id,
            role: 'system',
            content: e instanceof Error ? e.message : 'Unable to send message',
            createdAt: now(),
          },
        ]);
      } finally {
        if (activeRequest.current === controller) {
          activeRequest.current = undefined;
        }
        setIsSending(false);
      }
    },
    [api, session],
  );

  const abortMessage = useCallback(() => {
    activeRequest.current?.abort();
  }, []);

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (!session) {
        return;
      }
      for (const file of files) {
        try {
          await api.uploadFile(session.id, file);
        } catch (e) {
          console.error('[GlobalAssistant] File upload failed', e);
        }
      }
    },
    [api, session],
  );

  const value: KnowledgeChatContextValue = {
    // Always show the current page's context, never whatever the last
    // updateSessionContext response happened to carry.
    session: session ? { ...session, focus: pageFocus } : session,
    messages,
    isSending,
    sendMessage,
    abortMessage,
    uploadFiles,
    resetSession,
  };

  return (
    <KnowledgeChatContext.Provider value={value}>
      {children}
    </KnowledgeChatContext.Provider>
  );
};
